import { Suspense, lazy } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { IconBuilder } from '@components/icons/builder';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconCloseRegular } from '@components/icons/close-regular';
import { Stack } from '@components/stack';
import { Tag } from '@components/tag';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { PlanData } from 'api/ai-builder/requests';

const MarkdownText = lazy(() => import('@components/markdown-text'));

type PlanStatus = 'pending' | 'accepted' | 'rejected';

type AiBuilderPlanProps = {
  plan: PlanData;
  status: PlanStatus;
  onAccept: () => Promise<void>;
  onReject: () => Promise<void>;
  isLoading?: boolean;
};

export function AiBuilderPlan({ plan, status, onAccept, onReject, isLoading }: AiBuilderPlanProps) {
  const { t } = useScopedTranslate(locale);
  const isPending = status === 'pending';
  const isAccepted = status === 'accepted';
  const isRejected = status === 'rejected';

  return (
    <PlanCard>
      <PlanHeader>
        <Stack orientation="horizontal" space={2} align="center">
          <IconBuilder color="elementPurple80" size={16} />
          <Text font="semibold14" text={t('suggestedPlan')} color="textHelper" />
        </Stack>
        {isAccepted && (
          <Tag
            variant="green"
            text={t('accepted')}
            size="small"
            icon={<IconCheckmark color="elementGreen100" size={16} />}
          />
        )}
        {isRejected && (
          <Tag
            variant="red"
            text={t('rejected')}
            size="small"
            icon={<IconCloseRegular color="elementRed100" size={16} />}
          />
        )}
      </PlanHeader>

      <SummarySection>
        <Suspense fallback={<Text font="regular14" text={plan.summary} color="textPrimary" />}>
          <MarkdownText text={plan.summary} />
        </Suspense>
      </SummarySection>

      <PlanSection>
        <Suspense fallback={<PlanContent>{plan.plan}</PlanContent>}>
          <MarkdownText text={plan.plan} />
        </Suspense>
      </PlanSection>

      {isPending && (
        <ButtonRow>
          <Button variant="ghost" onClick={onReject} disabled={isLoading}>
            {t('reject')}
          </Button>
          <Button variant="primary" onClick={onAccept} disabled={isLoading}>
            {isLoading ? t('accepting') : t('accept')}
          </Button>
        </ButtonRow>
      )}
    </PlanCard>
  );
}

const PlanCard = styled.div({
  width: '100%',
  border: `1px solid ${theme.color.border10}`,
  borderRadius: pxToRem(8),
  padding: pxToRem(16),
  background: theme.color.backgroundLightDark,
});

const PlanHeader = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: pxToRem(12),
});

const SummarySection = styled.div({
  marginBottom: pxToRem(16),
  padding: pxToRem(12),
  background: theme.color.backgroundPrimary,
  borderRadius: pxToRem(4),
});

const PlanSection = styled.div({
  marginBottom: pxToRem(16),
  padding: pxToRem(12),
  background: theme.color.backgroundPrimary,
  borderRadius: pxToRem(4),
});

const PlanContent = styled.pre({
  margin: 0,
  whiteSpace: 'pre-wrap',
  wordWrap: 'break-word',
  fontFamily: 'inherit',
  fontSize: pxToRem(14),
  lineHeight: 1.5,
  color: theme.color.textPrimary,
});

const ButtonRow = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'flex-end',
  space: 2,
})({
  marginTop: pxToRem(8),
  paddingTop: pxToRem(12),
  borderTop: `1px solid ${theme.color.border10}`,
});

const locale = {
  en: {
    suggestedPlan: 'Suggested Plan',
    accept: 'Accept & Build',
    accepting: 'Accepting...',
    reject: 'Reject',
    accepted: 'Accepted',
    rejected: 'Rejected',
  },
};
